/**
 * Pure helpers shared by the browser snippet and the server (apps/web imports this file).
 * Keep free of DOM globals.
 */

export type Origin = "AI_GENERATED" | "AI_EDITED" | "HUMAN";
export type MarkKind = "TEXT" | "IMAGE" | "ARTICLE";

/** cyrb53: fast, synchronous 53-bit string hash → base36 */
export function hash(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/** Collapse whitespace, strip zero-width chars, lowercase. */
export function normalizeText(text: string): string {
  return text.replace(/[​-‍﻿]/g, "").replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Normalize an image URL so that CDN/CMS variants map to the same fingerprint:
 * drop query/hash, protocol and "www.", drop WordPress size suffix (-300x200), drop "-scaled".
 */
export function normalizeSrc(src: string, base?: string): string {
  let u: URL;
  try {
    u = new URL(src, base);
  } catch {
    return src.trim();
  }
  const host = u.hostname.toLowerCase().replace(/^www\./, "");
  const path = decodeURIComponent(u.pathname)
    .replace(/-\d{2,5}x\d{2,5}(?=\.[a-z0-9]+$)/i, "")
    .replace(/-scaled(?=\.[a-z0-9]+$)/i, "")
    .replace(/@\dx(?=\.[a-z0-9]+$)/i, "");
  return `${host}${path}`.toLowerCase();
}

/** Page path used to scope marks: pathname without trailing slash (except root). */
export function normalizePath(pathname: string): string {
  const p = pathname.replace(/\/{2,}/g, "/").replace(/\/index\.(html?|php)$/i, "/");
  return p.length > 1 ? p.replace(/\/$/, "") : "/";
}

export const textFingerprint = (text: string) => `t_${hash(normalizeText(text))}`;
export const srcFingerprint = (src: string, base?: string) => `i_${hash(normalizeSrc(src, base))}`;
/** Whole-article mark: one per page path */
export const articleFingerprint = (pathname: string) => `a_${hash(normalizePath(pathname))}`;
/** Whole-site article label (pagePath "*"): applies to every page that has no page-level article label */
export const SITE_ARTICLE_FP = "a_site";

export const IPTC_SOURCE: Record<Origin, string> = {
  AI_GENERATED: "http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia",
  AI_EDITED: "http://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia",
  HUMAN: "http://cv.iptc.org/newscodes/digitalsourcetype/digitalCapture",
};

export interface PublicMark {
  id: string;
  kind: MarkKind;
  pagePath: string;
  selector: string;
  fingerprint: string;
  origin: Origin;
  tool: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface PublicPolicy {
  /** "eu": official EU icons (Code of Practice, Annex 1). "aimarker": legacy AIMARKER pill. */
  labelStyle: "eu" | "aimarker";
  /** EU icon colour variant */
  euTone: "auto" | "black" | "white" | "black-50" | "white-50";
  /** Text label position inside the block: start (EU guidance: before/near the content) or end */
  textPlacement: "start" | "end";
  showImageBadge: boolean;
  showTextLabel: boolean;
  hideReviewedText: boolean;
  showHumanLabel: boolean;
  badgePosition: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  theme: "dark" | "light";
  accent: string;
  locale: "auto" | "pl" | "en";
  agencyName?: string;
  showTransparencyLink: boolean;
  /** Clickable label with a details card (second layer, optional under the Code of Practice) */
  showDetails?: boolean;
  industry?: "general" | "medical" | "finance_legal" | "news" | "ecommerce";
  labelTone?: "standard" | "soft";
  reviewerTitle?: string;
  explainer?: string;
}

export interface PublicSitePayload {
  site: { publicId: string; name: string; transparencyUrl: string };
  policy: PublicPolicy;
  marks: PublicMark[];
}

/**
 * Offline bundle: every label of a site in one file, hosted on the client's own server.
 * The snippet falls back to it (data-static) when the AIMARKER API is unreachable or the account is gone,
 * or uses it exclusively with data-mode="static".
 */
export interface StaticBundle {
  format: "aimarker-static";
  version: 1;
  generatedAt: string;
  site: PublicSitePayload["site"];
  policy: PublicPolicy;
  /** Page path → labels scoped to that page */
  pages: Record<string, PublicMark[]>;
  /** Labels shown on every page (site-wide marks, library images) */
  siteWide: PublicMark[];
}
export * from "./assess";
